import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Image as ImageIcon, Upload, X, AlertCircle } from 'lucide-react';
import { adminApi, formatApiError, resolveImageUrl, CATALOG_TYPES } from '../../utils/adminApi';

const TYPE_LABEL = { fabric: 'Fabric', embroidery: 'Embroidery' };

const CatalogTab = ({ onToast }) => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);

  const refresh = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.listCatalog(filter === 'all' ? undefined : filter);
      setItems(data);
    } catch (err) {
      setError(formatApiError(err, 'Could not load catalog.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filter]);

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    try {
      await adminApi.deleteCatalog(item.id);
      onToast?.('Removed');
      refresh();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  return (
    <div data-testid="catalog-tab">
      <div className="admin-section-actions" style={{ justifyContent: 'space-between' }}>
        <div className="admin-filter-group">
          {[
            { id: 'all', label: 'All' },
            { id: 'fabric', label: 'Fabrics' },
            { id: 'embroidery', label: 'Embroideries' },
          ].map((f) => (
            <button
              key={f.id}
              className={`admin-filter-pill ${filter === f.id ? 'active' : ''}`}
              onClick={() => setFilter(f.id)}
              data-testid={`catalog-filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <button className="btn-primary" onClick={() => setEditing({})} data-testid="catalog-create-btn">
          <Plus size={14} /> New Catalog Item
        </button>
      </div>

      {error && <div className="admin-error"><AlertCircle size={14} /> {error}</div>}
      {loading && <p className="body-regular">Loading…</p>}

      {!loading && !items.length && (
        <p className="body-regular admin-empty">No catalog items yet. Add fabrics or embroidery techniques.</p>
      )}

      {!loading && items.length > 0 && (
        <div className="admin-product-grid" data-testid="catalog-grid">
          {items.map((c) => (
            <article key={c.id} className="admin-product-card" data-testid={`catalog-card-${c.id}`}>
              <div className="admin-product-img">
                {(c.images?.[0] || c.image) ? (
                  <img src={resolveImageUrl(c.images?.[0] || c.image)} alt={c.name} />
                ) : (
                  <div className="admin-product-img-placeholder"><ImageIcon size={32} /></div>
                )}
              </div>
              <div className="admin-product-body">
                <span className="admin-pill admin-pill-type">{TYPE_LABEL[c.type] || c.type}</span>
                <h3 className="heading-3" style={{ marginTop: 10 }}>{c.name}</h3>
                {c.origin && <p className="body-small" style={{ color: 'var(--accent-gold)', fontStyle: 'italic' }}>{c.origin}</p>}
                <p className="body-small admin-product-desc">{c.description}</p>
                <div className="admin-product-meta-list">
                  {c.availability && <span><strong>Availability:</strong> {c.availability}</span>}
                  {c.lead_time && <span><strong>Lead time:</strong> {c.lead_time}</span>}
                  {c.price_range && <span><strong>Price range:</strong> {c.price_range}</span>}
                  {c.hours && <span><strong>Hours:</strong> {c.hours}</span>}
                  {c.craftsmen && <span><strong>Artisans:</strong> {c.craftsmen}</span>}
                </div>
                <div className="admin-product-actions">
                  <button className="btn-secondary admin-row-btn" onClick={() => setEditing(c)} data-testid={`catalog-edit-${c.id}`}>
                    <Pencil size={12} /> Edit
                  </button>
                  <button className="admin-icon-only" onClick={() => handleDelete(c)} data-testid={`catalog-delete-${c.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {editing && (
        <CatalogFormModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            onToast?.(editing.id ? 'Item updated' : 'Item created');
            refresh();
          }}
        />
      )}
    </div>
  );
};

const CatalogFormModal = ({ initial, onClose, onSaved }) => {
  const isEdit = !!initial.id;
  const initialImages = (initial.images && initial.images.length)
    ? initial.images
    : (initial.image ? [initial.image] : []);
  const [form, setForm] = useState({
    name: initial.name || '',
    type: initial.type || 'fabric',
    description: initial.description || '',
    images: initialImages,
    origin: initial.origin || '',
    availability: initial.availability || '',
    lead_time: initial.lead_time || '',
    price_range: initial.price_range || '',
    hours: initial.hours || '',
    craftsmen: initial.craftsmen || '',
  });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setErr('');
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const { url } = await adminApi.uploadImage(f);
        uploaded.push(url);
      }
      setForm((f) => ({ ...f, images: [...f.images, ...uploaded] }));
    } catch (e2) {
      setErr(formatApiError(e2, 'Upload failed.'));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeImage = (idx) => {
    setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
  };

  const moveImage = (idx, delta) => {
    setForm((f) => {
      const next = [...f.images];
      const target = idx + delta;
      if (target < 0 || target >= next.length) return f;
      [next[idx], next[target]] = [next[target], next[idx]];
      return { ...f, images: next };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setSubmitting(true);
    try {
      const isFabric = form.type === 'fabric';
      const payload = {
        name: form.name,
        type: form.type,
        description: form.description,
        image: form.images[0] || '',
        images: form.images,
        availability: form.availability || null,
        origin: isFabric ? (form.origin || null) : null,
        lead_time: isFabric ? (form.lead_time || null) : null,
        price_range: isFabric ? (form.price_range || null) : null,
        hours: !isFabric ? (form.hours || null) : null,
        craftsmen: !isFabric ? (form.craftsmen || null) : null,
      };
      if (isEdit) {
        await adminApi.updateCatalog(initial.id, payload);
      } else {
        await adminApi.createCatalog(payload);
      }
      onSaved();
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setSubmitting(false);
    }
  };

  const isFabric = form.type === 'fabric';

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()} data-testid="catalog-modal">
        <button className="admin-modal-close" onClick={onClose}><X size={20} /></button>
        <h2 className="heading-2" style={{ marginBottom: 24 }}>
          {isEdit ? 'Edit Catalog Item' : 'New Catalog Item'}
        </h2>

        <form onSubmit={submit} className="admin-form">
          <div className="admin-image-uploader admin-multi-uploader">
            <div className="admin-multi-header">
              <span className="admin-field-label">Catalog images</span>
              <span className="admin-multi-hint">First image is the cover. Drag-rearrange with arrows.</span>
            </div>
            <div className="admin-multi-grid">
              {form.images.map((img, idx) => (
                <div key={`${img}-${idx}`} className="admin-multi-tile" data-testid={`catalog-image-tile-${idx}`}>
                  <img src={resolveImageUrl(img)} alt={`catalog ${idx + 1}`} />
                  {idx === 0 && <span className="admin-multi-badge">Cover</span>}
                  <div className="admin-multi-actions">
                    <button type="button" onClick={() => moveImage(idx, -1)} title="Move left" disabled={idx === 0}>‹</button>
                    <button type="button" onClick={() => moveImage(idx, 1)} title="Move right" disabled={idx === form.images.length - 1}>›</button>
                    <button type="button" onClick={() => removeImage(idx)} title="Remove" data-testid={`catalog-image-remove-${idx}`}><X size={12} /></button>
                  </div>
                </div>
              ))}
              <label className="admin-multi-add">
                <Upload size={20} />
                <span>{uploading ? 'Uploading…' : 'Add images'}</span>
                <input type="file" accept="image/jpeg,image/png" multiple onChange={handleFile} disabled={uploading} hidden data-testid="catalog-image-input" />
              </label>
            </div>
            {form.images.length === 0 && (
              <div className="admin-multi-empty"><ImageIcon size={28} /> No images yet — add at least one.</div>
            )}
          </div>

          <div className="admin-form-grid">
            <label className="admin-field">
              <span className="admin-field-label">Name</span>
              <input required value={form.name} onChange={update('name')} data-testid="catalog-input-name" />
            </label>
            <label className="admin-field">
              <span className="admin-field-label">Type</span>
              <select value={form.type} onChange={update('type')} data-testid="catalog-input-type">
                {CATALOG_TYPES.map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
              </select>
            </label>
            <label className="admin-field">
              <span className="admin-field-label">Availability</span>
              <input value={form.availability} onChange={update('availability')} placeholder="In Stock / Limited / Made to Order" data-testid="catalog-input-availability" />
            </label>
            {isFabric ? (
              <>
                <label className="admin-field">
                  <span className="admin-field-label">Origin</span>
                  <input value={form.origin} onChange={update('origin')} placeholder="Varanasi" data-testid="catalog-input-origin" />
                </label>
                <label className="admin-field">
                  <span className="admin-field-label">Lead time</span>
                  <input value={form.lead_time} onChange={update('lead_time')} placeholder="2–3 weeks" data-testid="catalog-input-leadtime" />
                </label>
                <label className="admin-field">
                  <span className="admin-field-label">Price range</span>
                  <input value={form.price_range} onChange={update('price_range')} placeholder="Premium / Classic" data-testid="catalog-input-pricerange" />
                </label>
              </>
            ) : (
              <>
                <label className="admin-field">
                  <span className="admin-field-label">Hours of work</span>
                  <input value={form.hours} onChange={update('hours')} placeholder="200–400 hours" data-testid="catalog-input-hours" />
                </label>
                <label className="admin-field">
                  <span className="admin-field-label">Artisans</span>
                  <input value={form.craftsmen} onChange={update('craftsmen')} placeholder="Master Karigars, Lucknow" data-testid="catalog-input-craftsmen" />
                </label>
              </>
            )}
          </div>

          <label className="admin-field">
            <span className="admin-field-label">Description</span>
            <textarea rows="4" value={form.description} onChange={update('description')} data-testid="catalog-input-description" />
          </label>

          {err && <div className="admin-error"><AlertCircle size={14} /> {err}</div>}

          <div className="admin-modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={submitting || uploading} data-testid="catalog-submit">
              {submitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Item'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CatalogTab;
